import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Check, Maximize2, ArrowRight } from "lucide-react";
import { useQuote } from "@/context/QuoteContext";
import { productPath } from "@/data/products";
import ImageLightbox from "@/components/ImageLightbox";

const ProductCard = ({ product, zoomable = false }) => {
  const { requestQuote, items } = useQuote();
  const [zoom, setZoom] = useState(false);
  const inList = items.some((p) => p.id === product.id);
  const contain = product.imageFit === "contain";

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-surface transition-colors duration-500 hover:border-brand/40"
    >
      <button
        type="button"
        disabled={!zoomable}
        onClick={() => setZoom(true)}
        data-testid={`product-zoom-${product.id}`}
        aria-label={`Ingrandisci ${product.name}`}
        style={product.imageBg ? { background: product.imageBg } : undefined}
        className={`relative block aspect-square overflow-hidden disabled:cursor-default ${zoomable ? "cursor-zoom-in" : ""} ${contain ? "bg-gradient-to-b from-[#e2e2e2] to-[#c4c4c4]" : ""}`}
      >
        <img
          src={product.image}
          alt={product.alt}
          loading="lazy"
          data-testid={`product-image-${product.id}`}
          className={`h-full w-full transition-transform duration-700 ease-out group-hover:scale-105 ${
            contain ? "object-contain py-4" : "object-cover opacity-90"
          }`}
        />
        {!contain && <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />}
        <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-ink/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/80 backdrop-blur">
          {product.category}
        </span>
        {zoomable && (
          <span className="absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-ink/70 text-white opacity-80 backdrop-blur transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-4 w-4" />
          </span>
        )}
      </button>
      {zoomable && <ImageLightbox product={product} open={zoom} onOpenChange={setZoom} />}

      <div className="flex flex-1 flex-col p-6">
        <span className="mb-3 self-start rounded border border-brand/30 bg-brand/10 px-2 py-0.5 font-display text-xs font-bold tracking-wider text-brand">
          {product.code}
        </span>
        <h3 className="font-display text-xl font-bold uppercase leading-snug text-white">
          <Link to={productPath(product)} data-testid={`product-link-${product.id}`} className="transition-colors hover:text-brand">
            {product.name}
          </Link>
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-white/60">
          {product.short_description}
        </p>

        {product.specs?.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {product.specs.map((s) => (
              <li
                key={s}
                className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white/60"
              >
                {s}
              </li>
            ))}
          </ul>
        )}

        <Link
          to={productPath(product)}
          data-testid={`product-details-${product.id}`}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md border border-white/15 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:border-white/40"
        >
          Scheda prodotto <ArrowRight className="h-4 w-4" />
        </Link>
        <button
          data-testid={`request-quote-${product.id}`}
          onClick={() => requestQuote(product)}
          className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
            inList
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : "bg-brand text-white hover:bg-brand-hover"
          }`}
        >
          {inList ? (
            <>
              <Check className="h-4 w-4" /> Nel preventivo
            </>
          ) : (
            <>
              <FileText className="h-4 w-4" /> Richiedi preventivo
            </>
          )}
        </button>
      </div>
    </article>
  );
};

export default ProductCard;
